import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Typography,
  IconButton,
  Checkbox,
  Tooltip,
  Box,
} from '@mui/material'
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import type { StoredDocument } from '../../../server/services/documentStorageService'
import { User } from '../../../types'

interface DocumentsTableProps {
  documents: StoredDocument[]
  members: User[]
  selectedDocuments: Set<string>
  updatingDocuments: Set<string>
  onSelectDocument: (documentId: string) => void
  onSelectAll: () => void
  onDownloadDocument: (documentId: string, fileName: string) => void
  onDeleteDocument: (documentId: string) => void
  onInvertedToggle: (
    userId: string,
    documentId: string,
    currentInverted: boolean,
  ) => void
}

export function DocumentsTable({
  documents,
  members,
  selectedDocuments,
  updatingDocuments,
  onSelectDocument,
  onSelectAll,
  onDownloadDocument,
  onDeleteDocument,
  onInvertedToggle,
}: DocumentsTableProps) {
  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedDocuments.size > 0 &&
                    selectedDocuments.size < documents.length
                  }
                  checked={
                    documents.length > 0 &&
                    selectedDocuments.size === documents.length
                  }
                  onChange={onSelectAll}
                />
              </TableCell>
              <TableCell>Evrak Adı</TableCell>
              <TableCell>Yükleyen Kullanıcı</TableCell>
              <TableCell>Yükleme Tarihi</TableCell>
              <TableCell>Boyut</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Yasaklı</TableCell>
              <TableCell>Hata</TableCell>
              <TableCell align="center">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Henüz evrak bulunmuyor
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              documents.map((document) => (
                <TableRow
                  key={document.id}
                  hover
                  selected={selectedDocuments.has(document.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDocuments.has(document.id)}
                      onChange={() => onSelectDocument(document.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {document.originalName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {members.find((m) => m.id === document.metadata.userId)
                        ?.email || 'Bilinmiyor'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(document.metadata.signedAt).toLocaleDateString(
                        'tr-TR',
                        {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        },
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(document.metadata.size / 1024).toFixed(1)} KB
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        document.metadata.status === 'signed'
                          ? 'İmzalandı'
                          : 'Başarısız'
                      }
                      color={
                        document.metadata.status === 'signed'
                          ? 'success'
                          : 'error'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={document.metadata.inverted}
                      onChange={() =>
                        onInvertedToggle(
                          document.metadata.userId,
                          document.id,
                          document.metadata.inverted,
                        )
                      }
                      disabled={updatingDocuments.has(document.id)}
                    />
                  </TableCell>
                  <TableCell>
                    {document.metadata.error && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ maxWidth: 200, wordBreak: 'break-word' }}
                      >
                        {document.metadata.error}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {document.metadata.status === 'signed' && (
                        <Tooltip title="İndir">
                          <IconButton
                            size="small"
                            onClick={() =>
                              onDownloadDocument(
                                document.id,
                                document.originalName,
                              )
                            }
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDeleteDocument(document.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
